import { Controller } from "react-hook-form";
import { TimePicker } from "react-accessible-time-picker";
import { useTranslation } from "react-i18next";
import { useState } from "react";

interface Props {
  control: any;
  idx: number;
  name: string;
  periodIdx?: number;
}

type PickerTime = {
  hour: string;
  minute: string;
  period?: "AM" | "PM";
};

export default function TimeInput({
  control,
  idx,
  name,
  periodIdx,
}: Props) {
  const isNewStructure = periodIdx !== undefined;

  const fieldName = isNewStructure
    ? `days.${idx}.periods.${periodIdx}.${name}`
    : `openingHours.${idx}.${name}`;

  return (
    <Controller
      name={fieldName}
      control={control}
      render={({ field }) => (
        <TimePickerField
          field={field}
          isNewStructure={isNewStructure}
        />
      )}
    />
  );
}

interface TimePickerFieldProps {
  field: any;
  isNewStructure: boolean;
}

function TimePickerField({
  field,
  isNewStructure,
}: TimePickerFieldProps) {
  const { t } = useTranslation();

  /**
   * Convert backend 24-hour format to TimePicker 12-hour format.
   *
   * Examples:
   * 00:30 -> 12:30 AM
   * 12:30 -> 12:30 PM
   * 14:30 -> 02:30 PM
   * 23:30 -> 11:30 PM
   */
  const parseTimeForPicker = (
    value: string | undefined
  ): PickerTime => {
    if (!value) {
      return {
        hour: "12",
        minute: "00",
        period: "AM",
      };
    }

    const [hourString = "00", minute = "00"] = value
      .slice(0, 5)
      .split(":");

    const hour24 = Number(hourString);

    const period: "AM" | "PM" =
      hour24 >= 12 ? "PM" : "AM";

    let hour12 = hour24 % 12;

    if (hour12 === 0) {
      hour12 = 12;
    }

    return {
      hour: String(hour12).padStart(2, "0"),
      minute: minute.padStart(2, "0"),
      period,
    };
  };

  /**
   * Convert TimePicker 12-hour format to backend 24-hour format.
   *
   * Examples:
   * 12:30 AM -> 00:30
   * 12:30 PM -> 12:30
   * 02:30 PM -> 14:30
   * 11:30 PM -> 23:30
   */
  const convertPickerTimeToForm = (
    value: PickerTime
  ): string => {
    let hour = Number(value.hour);

    if (value.period === "AM") {
      if (hour === 12) {
        hour = 0;
      }
    } else {
      if (hour !== 12) {
        hour += 12;
      }
    }

    return `${String(hour).padStart(2, "0")}:${String(
      value.minute
    ).padStart(2, "0")}`;
  };

  /**
   * Initial value.
   *
   * For the new structure:
   * Backend value is HH:mm, so convert it to 12-hour format.
   *
   * For the old structure:
   * Keep the old TimePicker behavior exactly as it was.
   */
  const [pickerValue, setPickerValue] =
    useState<PickerTime>(() => {
      if (isNewStructure) {
        return parseTimeForPicker(field.value);
      }

      return (
        field.value || {
          hour: "12",
          minute: "00",
          period: "AM",
        }
      );
    });

  const handleChange = (value: PickerTime) => {
    /**
     * IMPORTANT:
     *
     * Update the TimePicker state first.
     *
     * This allows manual typing such as:
     *
     * 02
     * 15
     *
     * without React immediately replacing
     * the value while the user is typing.
     */
    setPickerValue(value);

    /**
     * Old structure.
     *
     * Keep the old behavior unchanged.
     */
    if (!isNewStructure) {
      field.onChange(value);
      return;
    }

    const hour = Number(value.hour);
    const minute = Number(value.minute);

    /**
     * Validate hour.
     *
     * TimePicker is running in 12-hour mode,
     * so valid hours are 1-12.
     */
    const validHour =
      Number.isInteger(hour) &&
      hour >= 1 &&
      hour <= 12;

    /**
     * Validate minute.
     */
    const validMinute =
      Number.isInteger(minute) &&
      minute >= 0 &&
      minute <= 59;

    /**
     * Don't update React Hook Form
     * until the value is valid.
     */
    if (!validHour || !validMinute) {
      return;
    }

    /**
     * Store 24-hour format in React Hook Form.
     *
     * 12:50 AM -> 00:50
     * 12:50 PM -> 12:50
     * 02:50 PM -> 14:50
     */
    field.onChange(
      convertPickerTimeToForm(value)
    );
  };

  return (
    <TimePicker
      value={pickerValue}
      onChange={handleChange}
      is24Hour={false}
      label=""
      minuteStep={1}
      popoverColumnHourTitle={t("hours")}
      popoverColumnMinuteTitle={t("minutes")}
      classes={{
        container: "w-40",

        timePicker:
          "flex items-center gap-2 border rounded-xl p-1 shadow-md bg-white transition-all duration-150 outline-none focus:outline-none",

        timeInputs:
          "flex gap-2",

        timeInput:
          "w-14 p-1 text-center border rounded-md bg-gray-50 outline-none focus:outline-none",

        separator:
          "mx-1 text-gray-400 font-medium",

        popoverContent:
          "bg-white border rounded-xl shadow-xl p-3 z-50 max-h-[80vh] overflow-auto",

        popoverColumns:
          "flex gap-2",

        popoverColumn:
          "flex flex-col gap-1 h-auto overflow-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100",

        popoverColumnTitle:
          "text-gray-500 text-xs font-semibold mb-1 text-center",

        popoverItem:
          "p-1 rounded hover:bg-gray-100 cursor-pointer text-gray-700 text-sm text-center",

        popoverActiveItem:
          "bg-primary text-white font-semibold rounded text-sm text-center",

        timeTrigger:
          "flex-1 w-full flex items-center justify-center",
      }}
    />
  );
}